import { BookmarkTypeEnum } from "../types";
import type { ComponentI, LessonI, ModuleI, PageI } from "../types";
import type { CreateUpdateBookmarkRequest } from "../api/types";

/**
 * Builds the payload for creating a page bookmark (add to toolbox).
 * Used when adding current lesson page to toolbox from the Add to Calendar flow.
 */
export function buildPageBookmarkPayload(
  page: PageI,
  lesson: LessonI | undefined,
  module: ModuleI | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers: Record<string, any>,
): Omit<CreateUpdateBookmarkRequest, "type"> & { type: BookmarkTypeEnum.PAGE } {
  const currentPageIndex =
    lesson?.pages?.findIndex((p) => p._id === page._id) ?? -1;
  const components =
    currentPageIndex > 0
      ? (lesson?.pages
          ?.slice(0, currentPageIndex)
          .flatMap((p) => p.components) ?? [])
      : [];

  const freezeVars = page.freezeVariablesFromPreviousPage || [];
  const findComponentsByVariables = (
    comps: ComponentI[],
    freezeSet: Set<string>,
    foundMap: Record<string, ComponentI>,
  ): void => {
    for (const comp of comps) {
      const variable = comp.content?.variable;
      if (variable && freezeSet.has(variable)) {
        foundMap[variable] = comp;
      }
      if (
        comp.content?.componentsList &&
        Array.isArray(comp.content.componentsList)
      ) {
        findComponentsByVariables(
          comp.content.componentsList,
          freezeSet,
          foundMap,
        );
      }
    }
  };

  const frozenComponentsMap: Record<string, ComponentI> = {};
  if (freezeVars.length && components.length) {
    findComponentsByVariables(
      components,
      new Set(freezeVars),
      frozenComponentsMap,
    );
  }

  const frozenComponents = freezeVars
    .map((v) => frozenComponentsMap[v])
    .filter(Boolean);

  const frozenAnswers = freezeVars.reduce(
    (acc, variable: string) => {
      if (Object.prototype.hasOwnProperty.call(answers, variable)) {
        acc[variable] = answers[variable];
      }
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {} as Record<string, any>,
  );

  return {
    page: page._id,
    type: BookmarkTypeEnum.PAGE,
    module: module?._id,
    lesson: lesson?._id,
    frozenComponents,
    frozenAnswers,
  };
}
